import { TreeNode } from "@/types"
import cloneDeep from "lodash/cloneDeep"
import { TreeNodeVisibilityResult } from "@/types/treeNodeVisibilityResult"

export const treeFilter = {
	methods: {

		getFilterMatches (flatTree: TreeNode<any>[], filter: string): TreeNode<any>[] {
			const lowerFilter = filter.toLowerCase()
			return filter === "" ? flatTree : flatTree.filter(item => item.node.title.toLowerCase().indexOf(lowerFilter) > -1)
		},
		organiseFilterResults (filterMatches: TreeNode<any>[]): TreeNode<any>[] {
			if (!filterMatches?.length) return []

			const nodeMap = new Map<string, TreeNode<any>>()
			const pendingChildLinks = new Map<string, string[]>()
			const searchResults: TreeNode<any>[] = []

			// First pass: create all nodes and record parent-child relationships
			for (const originalItem of filterMatches) {
				// Collect parent chain
				const parentChain: TreeNode<any>[] = []
				let current: TreeNode<any> | undefined = originalItem

				while (current) {
					parentChain.unshift(current) // Add to front
					current = current.parent
					if (!current) break
				}

				// Process all nodes in chain from root to leaf
				for (const item of parentChain) {
					const id = item.node.id

					// Skip if already processed
					if (nodeMap.has(id)) continue

					// Create clone
					const clone: TreeNode<any> = {
						node: { ...item.node },
						children: [],
						visible: item.visible
					}

					nodeMap.set(id, clone)

					// Record pending child link
					if (item.parent?.node) {
						const parentId = item.parent.node.id
						if (!pendingChildLinks.has(parentId)) {
							pendingChildLinks.set(parentId, [])
						}
                        pendingChildLinks.get(parentId)!.push(id)
					} else {
						// Top-level item
						searchResults.push(clone)
					}
				}
			}

			// Second pass: establish parent-child relationships
			for (const [parentId, childIds] of pendingChildLinks.entries()) {
				const parent = nodeMap.get(parentId)
				if (!parent) continue

				let existingChildIds: Set<any> = new Set()
				// Add unique children
				if (parent.children) {
					existingChildIds = new Set(parent.children.map(c => c.node.id))

					for (const childId of childIds) {
						const child = nodeMap.get(childId)
						if (!child || existingChildIds.has(childId)) continue

						child.parent = parent
						parent.children.push(child)
						existingChildIds.add(childId)
					}
				}
			}

			return searchResults
		},
		setTreeVisibility (tree: TreeNode<any>[]): TreeNodeVisibilityResult {
			let flatList: TreeNode<any>[] = []
			let treeStructure: TreeNode<any>[] = []

			if (!tree || tree.length === 0) {
				return { flatList, treeStructure }
			}

			const stack: Array<TreeNode<any>> = []

			// Add all top-level nodes to stack and structure
			for (let i = 0; i < tree.length; i++) {
				stack.push(tree[i])
				treeStructure.push(tree[i])
			}

			while (stack.length > 0) {
				const node = stack.pop()!

				// Add to flat list
				flatList.push(node)

				// Process children if any
				if (node.children && node.children.length > 0) {
					const childrenCount = node.children.length

					for (let i = childrenCount - 1; i >= 0; i--) {
						const child = node.children[i]
						// Set parent reference
						child.parent = node
						stack.push(child)
					}
				}
			}

			return { flatList, treeStructure }
		},
		doesNodeExistInChildren (children: TreeNode<any>[], nodeIdToFind: string) : boolean {
			return children.some(child => child.node.id === nodeIdToFind)
		},
		cloneTreeNodeWithoutChildren (treeNodeToClone: TreeNode<any>) : TreeNode<any> {
			let clone: TreeNode<any> = {
				node: cloneDeep(treeNodeToClone.node),
				children: [],
				visible: treeNodeToClone.visible
			}
			if (treeNodeToClone.parent) {
				clone.parent = this.cloneTreeNodeWithoutChildren(treeNodeToClone.parent)
			}
			return clone
		}
	}
}
